import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export interface MemoryCount {
  entities: number;
  documents: number;
  total: number;
}

export interface ToolCallRecord {
  id: string;
  sessionId: string | null;
  server: string;
  toolName: string;
  prefixedName: string;
  args: Record<string, unknown> | null;
  result: unknown;
  isError: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  /** ADR-0021: tier 1–4 that gated this call, or null for read-only / pre-rollout. */
  autonomyTier: number | null;
  /** allow | inform | approved | rejected | refused */
  autonomyDecision: string | null;
  createdAt: string;
}

export const inspectorKeys = {
  memoryCount: () => ['inspector', 'memory-count'] as const,
  toolCalls: (limit: number) => ['inspector', 'tool-calls', limit] as const,
};

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

export type TaskKind = 'tool' | 'skill' | 'delegate' | 'cron' | 'coding';

export interface ActiveTask {
  id: string;
  kind: TaskKind;
  name: string;
  sessionId?: string;
  startedAt: number;
  args?: Record<string, unknown>;
  /** Restate skillRunTracker key for long-running skill runs. */
  runId?: string;
  skillSlug?: string;
}

export interface FinishedTask extends ActiveTask {
  finishedAt: number;
  durationMs: number;
  isError: boolean;
  /** True when the user STOPPED the task (FRONT-186) — a clean halt, shown as a
   *  neutral "Stopped" card rather than a green success one. */
  cancelled?: boolean;
}

export interface SkillRunStatus {
  runId: string;
  skillSlug?: string;
  task?: string;
  status?: string;
  currentTurn?: number;
  maxTurns?: number;
  lastToolCall?: string;
  startedAt?: string;
  lastUpdate?: string;
  completedAt?: string;
  output?: string;
  externalId?: string;
  error?: string;
  /** FRONT-2260: the model this run actually used — the resolved pick, or
   *  the workspace default once a runtime fallback switched onto it.
   *  Absent on runs recorded before this field existed. */
  model?: string;
  /** FRONT-2260: set only once a pinned model was lost (unavailable at
   *  resolution time, or failed mid-run) and the run fell back to the
   *  workspace default — holds that lost pin's alias. */
  modelFallbackFrom?: string;
  /** FRONT-2260 fix round 1 (F5): true when `model` above is the
   *  workspace default. Drives the " (workspace default)" suffix on the
   *  "Ran on <X>" line — absent (not merely false) on a run recorded
   *  before this field existed. */
  modelIsDefault?: boolean;
  /** FRONT-2519: true when `model` is the model of the chat the run was
   *  started from — drives the " (chat model)" suffix. Absent unless the
   *  run picked the chat's model. */
  modelIsChat?: boolean;
}

function liveRefetchInterval(q: { state: { error: unknown; data: unknown } }): number | false {
  if (isAuthError(q.state.error)) return false;
  const s = (q.state.data as SkillRunStatus | undefined)?.status;
  // Once Restate reports terminal state, stop polling — the active-tasks
  // poll will reap the entry shortly after.
  if (s === 'completed' || s === 'failed') return false;
  return 3_000;
}

/** Statuses a run's tracker can never leave again (SkillRunResult's set). */
const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'turn-limit',
  'cancelled',
]);

function hasTerminalRunStatus(q: { state: { data: unknown } }): boolean {
  const s = (q.state.data as SkillRunStatus | undefined)?.status;
  return !!s && TERMINAL_RUN_STATUSES.has(s);
}

/**
 * The enable/poll/freshness decision for a skill run's status query,
 * pulled out as a pure function of (settled, runId) so it can be unit
 * tested without mounting anything (FRONT-2260).
 *
 * A LIVE (not yet settled) card keeps polling exactly as before — it's the
 * only way turn/tool progress reaches the card while the run is going.
 * A SETTLED card used to be `enabled: false` outright, so the "Ran on
 * <model>" line was never fetched for a card that mounts already finished.
 * Now it fetches without polling, and its cached answer counts as fresh
 * only once that answer is itself terminal: the same cache key is shared
 * with the live card, whose last poll may still say "running" (and name a
 * pinned model the run later fell back from), so a non-terminal cached
 * answer is refetched when the settled card mounts rather than trusted.
 */
export function skillRunStatusFetchPolicy(
  settled: boolean,
  runId: string | undefined,
): {
  enabled: boolean;
  refetchInterval: false | typeof liveRefetchInterval;
  staleTime: number | ((q: { state: { data: unknown } }) => number);
  maxRetries: number | null;
} {
  return {
    enabled: !!runId,
    refetchInterval: settled ? false : liveRefetchInterval,
    staleTime: settled ? (q) => (hasTerminalRunStatus(q) ? Infinity : 0) : 0,
    // A kept card's status is a nicety; don't retry it indefinitely while
    // Restate is down (null = the unbounded live behaviour).
    maxRetries: settled ? 2 : null,
  };
}

/**
 * `useSkillRunStatus(runId, enabled)` — the ORIGINAL, unchanged calling
 * convention: a plain visibility/mount gate (used by the three dialogs,
 * which stay open-gated and keep polling live exactly as before; they have
 * no "settled" concept of their own — SkillProgressDialog in particular is
 * the LIVE dialog and must keep polling for as long as it's open).
 *
 * `useSkillRunStatus(runId, { settled })` — the FRONT-2260 fix round 1 (F2)
 * convention: used by the Background tasks card, where `settled` decides
 * fetch-once-and-cache vs. keep-polling via {@link skillRunStatusFetchPolicy}.
 * `enabled` there is always "fetch when there's a runId" — a kept card is
 * always mounted, so there's no separate visibility gate to AND in.
 */
export function useSkillRunStatus(
  runId: string | undefined,
  enabledOrPolicy: boolean | { settled: boolean },
) {
  const usesPolicy = typeof enabledOrPolicy === 'object';
  const policy = usesPolicy
    ? skillRunStatusFetchPolicy(enabledOrPolicy.settled, runId)
    : {
        enabled: !!runId && enabledOrPolicy,
        refetchInterval: liveRefetchInterval,
        staleTime: 0,
        maxRetries: null,
      };
  return useQuery({
    queryKey: ['inspector', 'skill-status', runId ?? ''],
    queryFn: () => api.get<SkillRunStatus>(`/api/skill-status/${encodeURIComponent(runId!)}`),
    enabled: policy.enabled,
    refetchInterval: policy.refetchInterval,
    staleTime: policy.staleTime,
    retry: (count, err) =>
      !isAuthError(err) && (policy.maxRetries === null || count < policy.maxRetries),
  });
}

export interface BackgroundTasksResponse {
  active: ActiveTask[];
  recent: FinishedTask[];
  counts: { active: number; recent: number };
}

export function useBackgroundTasks() {
  return useQuery({
    queryKey: ['inspector', 'background-tasks'],
    queryFn: () => api.get<BackgroundTasksResponse>('/api/inspector/background-tasks'),
    // Poll fast while active so the panel feels live, slow when idle.
    refetchInterval: (q) => {
      if (isAuthError(q.state.error)) return false;
      const data = q.state.data as BackgroundTasksResponse | undefined;
      return (data?.counts.active ?? 0) > 0 ? 2_000 : 8_000;
    },
    retry: (_count, err) => !isAuthError(err),
  });
}

export function useMemoryCount() {
  return useQuery({
    queryKey: inspectorKeys.memoryCount(),
    queryFn: () => api.get<MemoryCount>('/api/inspector/memory-count'),
    staleTime: 30_000,
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : 60_000),
    retry: (_count, err) => !isAuthError(err),
  });
}

export function useRecentToolCalls(limit = 25) {
  return useQuery({
    queryKey: inspectorKeys.toolCalls(limit),
    queryFn: async () => {
      const r = await api.get<{ items: ToolCallRecord[] }>(
        `/api/inspector/tool-calls?limit=${limit}`,
      );
      return r.items ?? [];
    },
    staleTime: 10_000,
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : 30_000),
    retry: (_count, err) => !isAuthError(err),
  });
}
